// TapPay SDK wrapper service


/**
 * Check if TapPay SDK is loaded and available
 * @returns {boolean} True if SDK is loaded
 */
export const isSDKLoaded = () => (typeof window !== 'undefined' && typeof window.TPDirect !== 'undefined');

/**
 * Initialise TapPay SDK with environment variables
 * @returns {boolean} True if initialisation successful
 */
export const initialiseSDK = () => {
  if (!isSDKLoaded()) { return 'TapPay SDK not loaded'; }
  
  try {
    // Initialise SDK
    window.TPDirect.setupSDK(
      process.env.REACT_APP_TAPPAY_APP_ID,
      process.env.REACT_APP_TAPPAY_APP_KEY,
      'production'
    );
    
    return true;
  } catch (error) {
    console.log('Error initialising TapPay SDK:', error);

    return error;
  }
};

/**
 * Wrapper function to safely access TapPay SDK with error handling
 * @param {Function} callback Function to execute with SDK
 * @returns {*} Result of callback or false if error
 */
export const withSDK = (callback) => {
  if (!isSDKLoaded()) {
    console.error('TapPay SDK not loaded');
    
    return false;
  }
  
  // Initialise SDK if not already initialised
  if (!window.TPDirect.card && !initialiseSDK()) { return false; }

  try {
    return callback(window.TPDirect);
  } catch (error) {
    console.error('Error executing TapPay SDK operation:', error);
    
    return false;
  }
};

const style = (theme) => {
  const txtColour = (theme === 'dark') ? '#fff' : '#000';

  return ({
  'input': {
    'font-weight': 300,
    'color': txtColour,
    'font-family': 'Source Han Sans TC, source-han-sans-tc, Noto Sans TC, Open Sans, sans-serif',
    'font-size': '14.5px',
  },
  // style focus state
  ':focus': {
    
  },
  // style valid state
  '.valid': {
    'color': txtColour,
  },
  // style invalid state
  '.invalid': {
    'color': txtColour,
  },
  // Media queries
  // Note that these apply to the iframe, not the root window.
//   '@media screen and (max-width: 400px)': {
//     'input': {
//       'color': 'orange'
//     }
//   }
})};

export const getCreditCardForm = (theme) => {
  if (!isSDKLoaded()) { return; }

  try {
    withSDK(
      (sdk) => sdk.card.setup({
        fields: {
          number: {
            element: '#card-number', // CSS selector
            placeholder: '**** **** **** ****',
          },
          expirationDate: {
            element: '#card-expiration-date',
            placeholder: 'MM / YY',
          },
          ccv: {
            element: '#card-ccv',
            placeholder: 'ccv',
          },
        },
        styles: style(theme),
        isMaskCreditCardNumber: true,
        maskCreditCardNumberRange: { beginIndex: 6, endIndex: 11 },
      })
    );
  } catch (error) {
    console.error('Error setting up TapPay form:', error);
  }
};

/**
 * Set up card validation handler with TapPay SDK
 * @param {Function} onUpdate Callback for validation updates
 * @returns {boolean} True if setup successful
 */
export const validateCard = (onUpdate) => {
  try {
    withSDK(
      (sdk) => sdk.card.onUpdate((update) => {
        onUpdate({
          can_get_prime: update.canGetPrime,
          number: update.status.number,
          expiry: update.status.expiry,
          ccv: update.status.ccv,
        });
      })
    );
  } catch (error) {
    console.log('Error setting up validation handler:', error);

    return error;
  }
};

/**
 * Handle payment with TapPay SDK
 * @returns {Promise<Object>} Payment result
 */
export const getPrime = (callback) => {
  try {
    withSDK((sdk) => {
      sdk.card.getPrime((result) => {
        if (result.status !== 0) {
          callback({ success: false, error: result.msg });

          return;
        }

        callback({
          success: true,
          card: result.card,
          // prime: result.card.prime,
          // card_identifier: result.card_identifier,
          // cardinfo: {
          //   bank_id: result.card.bank_id,
          //   bincode: result.card.bincode,
          //   country: result.card.country,
          //   countrycode: result.card.countrycode,
          //   funding: result.card.funding,
          //   issuer: result.card.issuer,
          //   issuer_zh_tw: result.card.issuer_zh_tw,
          //   lastfour: result.card.lastfour,
          //   level: result.card.level,
          //   type: result.card.type,
          // },
        });
      });
    });
  } catch (error) {
    console.log('Error getting prime:', error);

    callback({
      success: false,
      error: error.message || 'Failed to get prime',
    });
  }
};